import { Injectable } from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { ApsPresentationView } from '../models/bg.model';
import {
  createBgPropertiesPanel,
  filterViewerProperties,
} from './viewer-properties-panel';

declare var Autodesk: any;

@Injectable({
  providedIn: 'root',
})
export class View3dService {
  private viewer: any;
  private panel: any;
  error?: string;

  initViewer(freshToken: string, modelIdToView: string, apsViewnames?: ApsPresentationView[]) {

    const options = {
      env: 'AutodeskProduction',
      getAccessToken: (onTokenReady: any) => {
        const token = freshToken;
        const timeInSeconds = 12000; // Use value provided by APS Authentication (OAuth) API
        onTokenReady(token, timeInSeconds);
      },
    };

    Autodesk.Viewing.Initializer(options, () => {

      const htmlDiv = document.getElementById('forgeViewer') as HTMLElement;
      this.viewer = new Autodesk.Viewing.GuiViewer3D(htmlDiv, {
        extensions: ['Autodesk.VisualClusters'],
      });

      const startedCode = this.viewer.start();
      if (startedCode > 0) {
        console.error('Failed to create a Viewer: WebGL not supported.');
        this.error = 'Failed to create a Viewer: WebGL not supported.';
        return;
      }

      console.log('Initialization complete, loading a model next...');

      this.panel = createBgPropertiesPanel(this.viewer);
      this.panel.setVisible(true);
      this._attachSelectionListener();
      this._attachToolbarButton();
      this._removeDefaultPropertiesButton();

      const dropdown = document.getElementById('models') as HTMLSelectElement;
      if (dropdown) {
        this.populateDropdown(dropdown, apsViewnames);

        dropdown.onchange = () => {
          const selectedView = dropdown.value;
          if (selectedView) {
            this.loadModelView(modelIdToView, selectedView);
          }
        };
      } else {
        console.error("Dropdown element with id 'models' not found.");
      }

      const selectedView = dropdown.value;
      this.loadModelView(modelIdToView, selectedView);

    });

  }

  private _attachToolbarButton(): void {
    const addButton = () => {
      const button = new Autodesk.Viewing.UI.Button('bg-props-toggle-btn');
      button.onClick = () => {
        this.panel?.setVisible(!this.panel?.isVisible());
      };
      button.setToolTip('Element Properties');
      button.setIcon('adsk-icon-properties');

      const group = new Autodesk.Viewing.UI.ControlGroup('bg-toolbar-group');
      group.addControl(button);
      this.viewer.toolbar.addControl(group);
    };

    if (this.viewer.toolbar) {
      addButton();
    } else {
      this.viewer.addEventListener(
        Autodesk.Viewing.TOOLBAR_CREATED_EVENT,
        addButton
      );
    }
  }

  private _removeDefaultPropertiesButton(): void {
    this.viewer.addEventListener(
      Autodesk.Viewing.EXTENSION_LOADED_EVENT,
      (e: any) => {
        if (e.extensionId === 'Autodesk.PropertiesManager') {
          const ext = this.viewer.getExtension('Autodesk.PropertiesManager');
          ext?._toolbarButton?.removeFromParent();
        }
      }
    );
  }

  private _attachSelectionListener(): void {
    this.viewer.addEventListener(
      Autodesk.Viewing.SELECTION_CHANGED_EVENT,
      (event: any) => {
        const ids: number[] = this.viewer.getSelection();

        if (!ids || ids.length === 0) {
          this.panel?.clearProperties();
          return;
        }

        const dbId = ids[0];
        const model = event.model ?? this.viewer.model;

        if (!model) {
          this.panel?.clearProperties();
          return;
        }

        model.getProperties(
          dbId,
          (result: any) => {
            const { bgProps, otherProps, layerGroups, presentationJson } = filterViewerProperties(
              result.properties ?? []
            );
            this.panel?.updateProperties(bgProps, otherProps, result.name, layerGroups, presentationJson);

            if (!this.panel?.isVisible()) {
              this.panel?.setVisible(true);
            }
          },
          () => {
            this.panel?.clearProperties();
          }
        );
      }
    );
  }

  private populateDropdown(dropdown: HTMLSelectElement, apsViewnames?: ApsPresentationView[]) {
    dropdown.innerHTML = '';

    if (apsViewnames) {
      apsViewnames.forEach(view => {
        const option = document.createElement('option');
        option.value = view.viewName || '';
        option.textContent = view.presentaionViewName || view.viewName || '';
        dropdown.appendChild(option);
      });
    }
  }

  private loadModel(modelIdToView: string) {
    Autodesk.Viewing.Document.load(
      modelIdToView,
      (doc: any) => this.onDocumentLoadSuccess(doc),
      this.onDocumentLoadFailure
    );
  }

  private loadModelView(modelIdToView: string, view: string) {
    Autodesk.Viewing.Document.load(
      modelIdToView,
      (doc: any) => this.onDocumentLoadSuccessView(doc, view),
      this.onDocumentLoadFailure
    );
  }

  private onDocumentLoadSuccess(viewerDocument: any) {
    const defaultModel = viewerDocument.getRoot().getDefaultGeometry();

    if (defaultModel) {
      this.viewer.loadDocumentNode(viewerDocument, defaultModel);
    } else {
      console.error('No suitable geometry found in the document.');
    }
  }

  private onDocumentLoadSuccessView(viewerDocument: any, view: string) {
    const defaultModel = viewerDocument.getRoot().search({
      type: 'geometry',
      name: view,
    })[0];

    if (defaultModel) {
      this.viewer.loadDocumentNode(viewerDocument, defaultModel);
    } else {
      console.error('No suitable geometry found in the document.');
    }
  }

  private onDocumentLoadFailure() {
    console.error('Failed fetching Forge manifest');
  }
}
